import React, { useEffect, useMemo, useState } from "react";
import {
  LineChart, Line, XAxis, YAxis, Tooltip, CartesianGrid, Legend, ResponsiveContainer,
  RadarChart, Radar, PolarGrid, PolarAngleAxis, PolarRadiusAxis
} from "recharts";
import "./ArchetypeSimulator.css";

/* =========================================================
   1) Metrics (radar + line candidates)
   ========================================================= */
const METRICS = [
  "CEFR", "Pronunciation", "Fluency", "Vocabulary", "Grammar",
  "Logic", "Persuasion", "Culture", "Beginning", "Ending",
  "Pace", "Hesitation", "Pitch", "Emotion"
];

// we’ll plot a tasteful subset on the line chart to keep it readable
const LINE_METRICS = ["CEFR", "Fluency", "Pronunciation", "Vocabulary", "Grammar"];

/* =========================================================
   2) Archetype definitions (8 total) — ranges match your table
   ========================================================= */
const ARCHETYPES = [
  {
    name: "Accelerator 🚀",
    description: "Learns fast at first, then levels off.",
    params: {
      baseline: [30, 40],
      slope: [2, 4],
      volatility: [2, 4],
      breakthroughChance: 0.02,
      plateauFactor: 0.6,
      skillBias: { Vocabulary: 1.2, Pronunciation: 1.1, Logic: 0.8 }
    }
  },
  {
    name: "Grinder 🐢",
    description: "Slow but steady improvements across all skills.",
    params: {
      baseline: [45, 55],
      slope: [0.5, 1.5],
      volatility: [1, 3],
      breakthroughChance: 0.05,
      plateauFactor: 0.0,
      skillBias: {}
    }
  },
  {
    name: "Rollercoaster 🎢",
    description: "Big ups and downs, but growth overall.",
    params: {
      baseline: [45, 55],
      slope: [1, 2],
      volatility: [5, 8],
      breakthroughChance: 0.1,
      plateauFactor: 0.2,
      skillBias: { Pronunciation: 1.3, Vocabulary: 1.1 }
    }
  },
  {
    name: "Specialist 🎯",
    description: "Excels in some areas, struggles in others.",
    params: {
      baseline: [40, 70],
      slope: [1, 2],
      volatility: [2, 4],
      breakthroughChance: 0.15,
      plateauFactor: 0.0,
      skillBias: { Grammar: 1.3, Pace: 0.7 }
    }
  },
  {
    name: "Performer 🎭",
    description: "Expressive, but technical accuracy lags.",
    params: {
      baseline: [40, 70],
      slope: [1, 2],
      volatility: [2, 4],
      breakthroughChance: 0.2,
      plateauFactor: 0.3,
      skillBias: { Emotion: 1.3, Pitch: 1.2, Grammar: 0.8 }
    }
  },
  {
    name: "Fragmented 🧩",
    description: "Fits and starts: one area surges while another stalls.",
    params: {
      baseline: [35, 60],
      slope: [0.5, 2],
      volatility: [3, 6],
      breakthroughChance: 0.3,
      plateauFactor: 0.5,
      skillBias: {
        Vocabulary: 1.3,
        Grammar: 1.2,
        Pronunciation: 0.8,
        Hesitation: 0.7
      }
    }
  },
  {
    name: "Comeback Kid 🔥",
    description: "Starts strong, dips dramatically, then rebounds hard.",
    params: {
      baseline: [55, 60],
      slope: [1, 2],
      volatility: [3, 5],
      breakthroughChance: 0.4,
      plateauFactor: 0.0,
      skillBias: {
        Grammar: 1.2,
        Vocabulary: 1.2,
        Pace: 0.8,
        Hesitation: 1.1
      }
    }
  },
  {
    name: "Staller ⏸️",
    description: "Flatline with tiny wiggles.",
    params: {
      baseline: [50, 55],
      slope: [0, 0.5],
      volatility: [0, 1],
      breakthroughChance: 0.01,
      plateauFactor: 1.0,
      skillBias: {}
    }
  }
];

/* =========================================================
   3) Utils
   ========================================================= */
const rrand = (min, max) => Math.random() * (max - min) + min;

/* =========================================================
   4) Data generator (30 days) w/ Comeback Kid dip → rebound
   ========================================================= */
function generateData(archetype) {
  const { baseline, slope, volatility, breakthroughChance, plateauFactor, skillBias } = archetype.params;
  const out = [];
  const today = new Date();

  METRICS.forEach(metric => {
    let value = rrand(baseline[0], baseline[1]);

    for (let i = 29; i >= 0; i--) {
      const date = new Date(today);
      date.setDate(today.getDate() - i);

      // base slope
      let dailySlope = rrand(slope[0], slope[1]);
      // bias by skill
      const bias = (skillBias && metric in skillBias) ? skillBias[metric] : 1.0;
      dailySlope *= bias;

      // volatility larger early, calmer late
      let v = rrand(volatility[0], volatility[1]) * (i / 30);

      // Comeback Kid special: 1–10 light gain; 11–15 dip; 16–30 rebound
      if (archetype.name.includes("Comeback Kid")) {
        const dayIndex = 30 - i; // 1..30
        if (dayIndex <= 10) {
          dailySlope *= 0.5;
          v *= 1.2;
        } else if (dayIndex <= 15) {
          dailySlope = -rrand(0.5, 1.5);
          v *= 2.0;
        } else {
          dailySlope *= 2.0;
          v *= 0.5;
        }
      }

      // noise
      const noise = (Math.random() - 0.5) * v;

      // occasional breakthrough jump
      if (Math.random() < breakthroughChance / 30) {
        dailySlope += rrand(5, 15);
      }

      // occasional plateau
      if (Math.random() < plateauFactor / 30) {
        dailySlope *= 0.1;
      }

      // integrate & clamp
      value = Math.max(0, Math.min(100, value + dailySlope + noise));

      if (!out[i]) out[i] = { date: date.toLocaleDateString() };
      out[i][metric] = Math.round(value);
    }
  });

  return out;
}

/* =========================================================
   5) Build radar data from last day’s snapshot
   ========================================================= */
function toRadarData(chartData) {
  const last = chartData[chartData.length - 1] || {};
  return METRICS.map(m => ({
    metric: m,
    value: typeof last[m] === "number" ? last[m] : 0
  }));
}

/* =========================================================
   6) Typewriter hook (narrative)
   ========================================================= */
function useTypewriter(text, speed = 28) {
  const [displayed, setDisplayed] = React.useState("");
  const timerRef = React.useRef(null);
  const idxRef = React.useRef(0);
  const runIdRef = React.useRef(0); // cancel older runs

  const units = React.useMemo(() => {
    if (!text) return [];
    if (typeof Intl !== "undefined" && Intl.Segmenter) {
      const seg = new Intl.Segmenter(undefined, { granularity: "grapheme" });
      return Array.from(seg.segment(text), p => p.segment);
    }
    return Array.from(text); // fallback
  }, [text]);

  React.useEffect(() => {
    // cancel any previous run (StrictMode-safe)
    if (timerRef.current) clearInterval(timerRef.current);

    setDisplayed("");
    idxRef.current = 0;
    const myRun = ++runIdRef.current;

    timerRef.current = setInterval(() => {
      // aborted? (e.g., archetype changed)
      if (myRun !== runIdRef.current) return;

      const i = idxRef.current;
      if (i >= units.length) {
        clearInterval(timerRef.current);
        timerRef.current = null;
        return;
      }
      setDisplayed(prev => prev + units[i]); // includes spaces
      idxRef.current = i + 1;
    }, speed);

    return () => {
      if (timerRef.current) {
        clearInterval(timerRef.current);
        timerRef.current = null;
      }
    };
  }, [units, speed]);

  return displayed;
}


/* =========================================================
   7) Component
   ========================================================= */
export default function ArchetypeSimulator() {
  const [selected, setSelected] = useState(ARCHETYPES[0]);
  const [data, setData] = useState([]);
  const [animKey, setAnimKey] = useState(0); // forces recharts re-animate on change
  const typed = useTypewriter(selected.description, 28);

  // regenerate on archetype change
  useEffect(() => {
    setData(generateData(selected));
    setAnimKey(k => k + 1);
  }, [selected]);

  const radarData = useMemo(() => toRadarData(data), [data]);

  const params = selected.params;
  const fmtRange = ([a, b]) => `${Math.round(a)}–${Math.round(b)}`;
  const chronological = React.useMemo(() => [...data].reverse(), [data]);
  return (
    <section className="archetype-sim-container">
      <h3 className="sim-title">What's Your Fluency Development Style?</h3>

      {/* Tablist */}
      <div className="archetype-selector" role="tablist" aria-label="Archetypes">
        {ARCHETYPES.map(a=>{
          const tabId=`tab-${a.name.replace(/\s+/g,"-")}`;
          const panelId=`panel-${a.name.replace(/\s+/g,"-")}`;
          return(
            <button
              key={a.name}
              id={tabId}
              role="tab"
              aria-selected={a.name===selected.name}
              aria-controls={panelId}
              className={`archetype-btn ${a.name===selected.name?"active":""}`}
              onClick={()=>setSelected(a)}
            >
              {a.name}
            </button>
          );
        })}
      </div>

      <p className="archetype-description" aria-live="polite">
        {typed}
      </p>

      {/* Tabpanel */}
        <div
          id={`panel-${selected.name.replace(/\s+/g,"-")}`}
          role="tabpanel"
          aria-labelledby={`tab-${selected.name.replace(/\s+/g,"-")}`}
        >
            {/* LINE CHART */}
            <div className="chart-container wriggle" key={`line-${animKey}`}>
                <ResponsiveContainer width="100%" height={320}>
                <LineChart data={chronological} margin={{ top: 8, right: 20, bottom: 0, left: -8 }}>
                    <CartesianGrid strokeDasharray="3 3" opacity={0.25} />
                    <XAxis dataKey="date" tick={{ fontSize: 10 }} />
                    <YAxis domain={[0, 100]} tick={{ fontSize: 10 }} />
                    <Tooltip />
                    <Legend />
                    {LINE_METRICS.map((m, idx) => (
                    <Line
                        key={m}
                        type="monotone"
                        dataKey={m}
                        strokeWidth={2}
                        dot={false}
                        isAnimationActive
                        animationBegin={200 + idx * 120}
                        animationDuration={1000 + idx * 140}
                        strokeDasharray="4 6"            // lets CSS animate dash offset (wriggle)
                        className="line-series"
                    />
                    ))}
                </LineChart>
                </ResponsiveContainer>
            </div>

            {/* RADAR CHART */}
            <div className="chart-container radar-container" key={`radar-${animKey}`}>
                <ResponsiveContainer width="100%" height={320}>
                <RadarChart data={radarData}>
                    <PolarGrid />
                    <PolarAngleAxis dataKey="metric" tick={{ fontSize: 10 }} />
                    <PolarRadiusAxis angle={45} domain={[0, 100]} tick={{ fontSize: 10 }} />
                    <Radar
                    name={selected.name}
                    dataKey="value"
                    isAnimationActive
                    animationBegin={100}
                    animationDuration={1200}
                    stroke="rgba(255,255,255,0.85)"
                    fill="rgba(255, 64, 129, 0.35)"
                    />
                    <Legend />
                </RadarChart>
                </ResponsiveContainer>
            </div>

            {/* PARAM SNAPSHOT */}
            <div className="sim-params">
                <div className="sim-pill">Baseline: <strong>{fmtRange(params.baseline)}</strong></div>
                <div className="sim-pill">Slope: <strong>{fmtRange(params.slope)}</strong></div>
                <div className="sim-pill">Volatility: <strong>{fmtRange(params.volatility)}</strong></div>
                <div className="sim-pill">Breakthrough: <strong>{Math.round(params.breakthroughChance * 100)}%</strong></div>
                <div className="sim-pill">Plateau: <strong>{Math.round(params.plateauFactor * 100)}%</strong></div>
            </div>
      </div>
    </section>
  );
}